<template>
  <v-dialog v-model="dialogVisible" max-width="1200px" scrollable height="90vh">
    <v-card class="schedule-calendar-modal">
      <!-- 헤더 -->
      <div class="modal-header">
        <div class="header-left">
          <v-icon size="28" color="white">mdi-calendar-clock</v-icon>
          <div class="header-title-section">
            <div class="employee-navigator">
              <button 
                class="emp-nav-btn prev" 
                @click="navigateEmployee(-1)"
                :disabled="!canNavigatePrev"
                title="이전 직원"
              >
                <v-icon size="20">mdi-chevron-left</v-icon>
              </button>
              <div class="emp-selector-wrapper">
                <select v-model="currentEmployeeIndex" class="emp-select" @change="onEmployeeChange">
                  <option 
                    v-for="(emp, idx) in employeeList" 
                    :key="emp.emp_code || idx" 
                    :value="idx"
                  >
                    {{ emp.emp_name }} ({{ emp.emp_job }})
                  </option>
                </select>
                <span class="emp-counter">{{ currentEmployeeIndex + 1 }} / {{ employeeList.length }}</span>
              </div>
              <button 
                class="emp-nav-btn next" 
                @click="navigateEmployee(1)"
                :disabled="!canNavigateNext"
                title="다음 직원"
              >
                <v-icon size="20">mdi-chevron-right</v-icon>
              </button>
            </div>
          </div>
        </div>
        <div class="header-controls">
          <div class="year-month-selector">
            <button class="nav-btn" @click="changeMonth(-1)">
              <v-icon size="20">mdi-chevron-left</v-icon>
            </button>
            <div class="current-period">
              <select v-model="selectedYear" class="year-select">
                <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}년</option>
              </select>
              <select v-model="selectedMonth" class="month-select">
                <option v-for="m in 12" :key="m" :value="m">{{ m }}월</option>
              </select>
            </div>
            <button class="nav-btn" @click="changeMonth(1)">
              <v-icon size="20">mdi-chevron-right</v-icon>
            </button>
          </div>
          <button class="pdf-btn" @click="printSchedulePdf" :disabled="isPdfLoading || filteredScheduleData.length === 0" title="현재 직원 일정표 PDF 출력">
            <v-icon size="20" color="white">{{ isPdfLoading ? 'mdi-loading mdi-spin' : 'mdi-file-pdf-box' }}</v-icon>
            <span>PDF</span>
          </button>
          <button 
            class="batch-pdf-btn" 
            @click="printAllCaregiversPdf" 
            :disabled="isBatchPdfLoading || employeeList.length === 0" 
            title="전체 요양보호사 일정표 PDF 출력"
          >
            <v-icon size="20" color="white">{{ isBatchPdfLoading ? 'mdi-loading mdi-spin' : 'mdi-file-document-multiple' }}</v-icon>
            <span>전체</span>
          </button>
          <button class="close-btn" @click="close">
            <v-icon size="24" color="white">mdi-close</v-icon>
          </button>
        </div>
      </div>

      <!-- 뷰 선택 탭 -->
      <div class="view-tabs">
        <button 
          class="view-tab" 
          :class="{ active: activeView === 'caregiver' }"
          @click="activeView = 'caregiver'"
        >
          <v-icon size="20">mdi-account-hard-hat</v-icon>
          <span>{{ currentEmployee?.emp_name }} 근무일정</span>
        </button>
        <button 
          class="view-tab" 
          :class="{ active: activeView === 'patient' }"
          @click="activeView = 'patient'"
        >
          <v-icon size="20">mdi-account-heart</v-icon>
          <span>담당 수급자</span>
          <span class="count-badge">{{ uniquePatients.length }}</span>
        </button>
      </div>

      <!-- 컨텐츠 -->
      <div class="modal-content">
        <div v-if="isLoading" class="loading-state">
          <v-progress-circular indeterminate color="#1e3a8a" size="48" />
          <span>일정을 불러오는 중...</span>
        </div>

        <div v-else-if="filteredScheduleData.length === 0" class="empty-state">
          <v-icon size="64" color="#cbd5e1">mdi-calendar-blank</v-icon>
          <h3>일정 데이터가 없습니다</h3>
          <p>{{ selectedYear }}년 {{ selectedMonth }}월에 {{ currentEmployee?.emp_name }}님의 일정이 없습니다.</p>
        </div>

        <template v-else>
          <!-- 요양보호사(선택한 사원) 근무일정 뷰 -->
          <div v-if="activeView === 'caregiver'" class="calendar-grid-container">
            <div class="caregiver-info-bar">
              <div class="caregiver-badge">
                <v-avatar size="36" color="#1e3a8a">
                  <span class="avatar-text">{{ currentEmployee?.emp_name?.charAt(0) }}</span>
                </v-avatar>
                <div class="badge-info">
                  <span class="badge-name">{{ currentEmployee?.emp_name }}</span>
                  <span class="badge-job">{{ currentEmployee?.emp_job }}</span>
                </div>
              </div>
              <div class="quick-stats">
                <div class="stat">
                  <span class="stat-value">{{ getCaregiverMonthlyStats.totalDays }}</span>
                  <span class="stat-label">근무일</span>
                </div>
                <div class="stat">
                  <span class="stat-value">{{ getCaregiverMonthlyStats.totalHours }}</span>
                  <span class="stat-label">총 시간</span>
                </div>
                <div class="stat">
                  <span class="stat-value">{{ getCaregiverMonthlyStats.patientCount }}</span>
                  <span class="stat-label">담당 수급자</span>
                </div>
              </div>
            </div>
            
            <div class="calendar-wrapper">
              <div class="calendar-header">
                <div class="weekday" v-for="day in weekDays" :key="day">{{ day }}</div>
              </div>
              <div class="calendar-body">
                <div 
                  v-for="(day, idx) in calendarDays" 
                  :key="idx"
                  class="calendar-day"
                  :class="{ 
                    'other-month': !day.isCurrentMonth,
                    'today': day.isToday,
                    'has-schedule': getCaregiverSchedules(day.date).length > 0
                  }"
                >
                  <div class="day-header">
                    <div class="day-number">{{ day.day }}</div>
                    <span v-if="day.isToday" class="today-chip">TODAY</span>
                  </div>
                  <div v-if="day.isCurrentMonth" class="day-schedules">
                    <div 
                      v-for="(schedule, sIdx) in getCaregiverSchedules(day.date).slice(0, 4)" 
                      :key="sIdx"
                      class="schedule-chip-inline"
                      :class="{ 'highlighted': hoveredPatient === schedule.les_patient_name }"
                      :style="{ borderColor: getPatientColor(schedule.les_patient_name) }"
                      @mouseenter="hoveredPatient = schedule.les_patient_name"
                      @mouseleave="hoveredPatient = null"
                    >
                      <div class="chip-dot" :style="{ borderColor: getPatientColor(schedule.les_patient_name) }"></div>
                      <span class="chip-text">{{ schedule.les_patient_name }} {{ formatTime(schedule.les_start_time) }}-{{ formatTime(schedule.les_end_time) }}</span>
                    </div>
                    <div 
                      v-if="getCaregiverSchedules(day.date).length > 4" 
                      class="more-schedules"
                    >
                      +{{ getCaregiverSchedules(day.date).length - 4 }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 담당 수급자 목록 -->
            <div class="summary-section">
              <div class="patient-list-card">
                <h4>
                  <v-icon size="20" color="#0ea5e9">mdi-account-heart</v-icon>
                  담당 수급자 목록
                </h4>
                <div class="patient-chips">
                  <div 
                    v-for="pt in getCaregiverPatients" 
                    :key="pt.name"
                    class="patient-chip"
                    :style="{ borderColor: getPatientColor(pt.name) }"
                  >
                    <div class="chip-color" :style="{ backgroundColor: getPatientColor(pt.name) }"></div>
                    <span class="chip-name">{{ pt.name }}</span>
                    <span class="chip-count">{{ pt.count }}회</span>
                    <span class="chip-hours">{{ pt.hours }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 수급자별 뷰 (해당 사원이 담당하는 수급자만) -->
          <div v-if="activeView === 'patient'" class="calendar-grid-container">
            <!-- 수급자 정보 바 -->
            <div class="patient-info-bar">
              <div class="patient-selector-area">
                <label class="select-label">수급자 선택</label>
                <div class="select-wrapper">
                  <select v-model="selectedPatient" class="person-select">
                    <option v-for="patient in uniquePatients" :key="patient" :value="patient">
                      {{ patient }}
                    </option>
                  </select>
                  <span class="select-arrow">▼</span>
                </div>
                <span v-if="getPatientFamilyInfo.isFamily" class="family-chip">
                  가족요양 · {{ getPatientFamilyInfo.who }}
                </span>
              </div>
              <div class="patient-stats-row">
                <div class="stat-box">
                  <span class="stat-value">{{ getPatientMonthlyStats.totalDays }}</span>
                  <span class="stat-label">서비스일</span>
                </div>
                <div class="stat-box">
                  <span class="stat-value">{{ getPatientMonthlyStats.totalHours }}</span>
                  <span class="stat-label">총시간</span>
                </div>
                <div class="stat-box">
                  <span class="stat-value">{{ getPatientMonthlyStats.visitCount }}</span>
                  <span class="stat-label">방문</span>
                </div>
                <div class="stat-box suga">
                  <span class="stat-value">{{ formatMoney(getPatientTotalSuga) }}</span>
                  <span class="stat-label">수가총액(원)</span>
                </div>
              </div>
            </div>
            
            <div class="calendar-wrapper">
              <div class="calendar-header">
                <div class="weekday" v-for="day in weekDays" :key="day">{{ day }}</div>
              </div>
              <div class="calendar-body">
                <div 
                  v-for="(day, idx) in calendarDays" 
                  :key="idx"
                  class="calendar-day"
                  :class="{ 
                    'other-month': !day.isCurrentMonth,
                    'today': day.isToday,
                    'has-schedule': getPatientSchedulesForCaregiver(day.date).length > 0
                  }"
                >
                  <div class="day-header">
                    <div class="day-number">{{ day.day }}</div>
                    <span v-if="day.isToday" class="today-chip">TODAY</span>
                  </div>
                  <div class="day-schedules">
                    <div 
                      v-for="(schedule, sIdx) in getPatientSchedulesForCaregiver(day.date).slice(0, 3)" 
                      :key="sIdx"
                      class="schedule-chip caregiver"
                    >
                      <div class="chip-dot"></div>
                      <div class="chip-info">
                        <span class="chip-time">{{ formatTime(schedule.les_start_time) }}-{{ formatTime(schedule.les_end_time) }}</span>
                        <span class="chip-service">수가: {{ formatMoney(schedule.les_suga_money || 0) }}원</span>
                      </div>
                    </div>
                    <div 
                      v-if="getPatientSchedulesForCaregiver(day.date).length > 3" 
                      class="more-schedules"
                    >
                      +{{ getPatientSchedulesForCaregiver(day.date).length - 3 }}건
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';

interface ScheduleData {
  les_idx?: number;
  les_date: string;
  les_start_time: string;
  les_end_time: string;
  les_patient_name: string;
  les_patient_code?: string;
  les_caregiver_name: string;
  les_caregiver_birth?: string;
  les_caregiver_code?: string;
  les_caregiver_gubun?: string;
  les_familly_rel?: string;
  les_familly_who?: string;
  les_service_gubun?: string;
  les_suga_code?: string;
  les_suga_name?: string;
  les_suga_money?: number;
}

interface CalendarDay {
  day: number;
  date: string;
  isCurrentMonth: boolean;
  isToday: boolean;
}

interface Props {
  modelValue: boolean;
  employee: any;
  employees?: any[]; // 전체 직원 목록 (선택적)
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'change-employee', employee: any): void;
}>();

// 현재 선택된 직원 인덱스
const currentEmployeeIndex = ref(0);

// 직원 목록 (props.employees가 있으면 사용, 없으면 현재 직원만)
const employeeList = computed(() => {
  if (props.employees && props.employees.length > 0) {
    return props.employees;
  }
  return props.employee ? [props.employee] : [];
});

// 현재 선택된 직원
const currentEmployee = computed(() => {
  return employeeList.value[currentEmployeeIndex.value] || props.employee;
});

// 이전/다음 이동 가능 여부
const canNavigatePrev = computed(() => currentEmployeeIndex.value > 0);
const canNavigateNext = computed(() => currentEmployeeIndex.value < employeeList.value.length - 1);

// 직원 이동
const navigateEmployee = (delta: number) => {
  const newIndex = currentEmployeeIndex.value + delta;
  if (newIndex >= 0 && newIndex < employeeList.value.length) {
    currentEmployeeIndex.value = newIndex;
    emit('change-employee', currentEmployee.value);
    fetchScheduleData();
  }
};

// 직원 선택 변경
const onEmployeeChange = () => {
  emit('change-employee', currentEmployee.value);
  fetchScheduleData();
};

const store = useStore();
const user = computed(() => store.state.user);

// 상태
const isLoading = ref(false);
const isPdfLoading = ref(false);
const isBatchPdfLoading = ref(false);
const scheduleData = ref<ScheduleData[]>([]);
const activeView = ref<'patient' | 'caregiver'>('caregiver');
const hoveredPatient = ref<string | null>(null); // 호버된 수급자 이름

// 연월 선택
const today = new Date();
const selectedYear = ref(today.getFullYear());
const selectedMonth = ref(today.getMonth() + 1);
const selectedPatient = ref('');

// 다이얼로그 표시 상태
const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

// 연도 옵션 (현재 연도 기준 ±2년)
const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear();
  return [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];
});

// 요일 배열
const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

// 이미 백엔드에서 해당 사원의 일정만 필터링해서 가져옴
const filteredScheduleData = computed(() => {
  return scheduleData.value;
});

// 해당 사원이 담당하는 수급자 목록
const uniquePatients = computed(() => {
  const patients = new Set(filteredScheduleData.value.map(s => s.les_patient_name));
  return Array.from(patients).filter(Boolean).sort();
});

// 달력 일자 생성
const calendarDays = computed<CalendarDay[]>(() => {
  const year = selectedYear.value;
  const month = selectedMonth.value;
  
  const firstDay = new Date(year, month - 1, 1);
  const startDay = new Date(firstDay);
  startDay.setDate(startDay.getDate() - firstDay.getDay());
  
  const days: CalendarDay[] = [];
  const current = new Date(startDay);
  const todayStr = formatDateStr(today);
  
  for (let i = 0; i < 42; i++) {
    const isCurrentMonth = current.getMonth() === month - 1;
    const dateStr = formatDateStr(current);
    
    days.push({
      day: current.getDate(),
      date: dateStr,
      isCurrentMonth,
      isToday: dateStr === todayStr
    });
    
    current.setDate(current.getDate() + 1);
  }
  
  return days;
});

// 색상 팔레트 - 대비되는 순서로 배치
const patientColors: Record<string, string> = {};
const colorPalette = [
  '#ef4444', // 빨강
  '#3b82f6', // 파랑 (빨강과 대비)
  '#22c55e', // 초록 (파랑과 대비)
  '#a855f7', // 보라 (초록과 대비)
  '#f97316', // 주황 (보라와 대비)
  '#14b8a6', // 청록 (주황과 대비)
  '#ec4899', // 분홍 (청록과 대비)
  '#4f46e5', // 남색 (분홍과 대비)
  '#eab308', // 노랑 (남색과 대비)
  '#64748b', // 회색 (노랑과 대비)
];

const getPatientColor = (name: string): string => {
  if (!patientColors[name]) {
    const idx = Object.keys(patientColors).length % colorPalette.length;
    patientColors[name] = colorPalette[idx];
  }
  return patientColors[name];
};

// 날짜 포맷
const formatDateStr = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// 시간 포맷
const formatTime = (time?: string): string => {
  if (!time) return '';
  const str = time.toString().replace(/[^0-9]/g, '');
  if (str.length >= 4) {
    return `${str.slice(0, 2)}:${str.slice(2, 4)}`;
  }
  return time;
};

// 일정 날짜 포맷 (YYYYMMDD -> YYYY-MM-DD)
const formatScheduleDate = (date: string): string => {
  if (!date) return '';
  const str = date.toString().replace(/[^0-9]/g, '');
  if (str.length === 8) {
    return `${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6, 8)}`;
  }
  return date;
};

// 요양보호사(선택한 사원)의 일정 조회
const getCaregiverSchedules = (dateStr: string): ScheduleData[] => {
  return filteredScheduleData.value.filter(s => {
    const scheduleDate = s.les_date?.includes('-') ? s.les_date : formatScheduleDate(s.les_date);
    return scheduleDate === dateStr;
  });
};

// 수급자별 일정 조회 (해당 사원이 담당하는 것만)
const getPatientSchedulesForCaregiver = (dateStr: string): ScheduleData[] => {
  if (!selectedPatient.value) return [];
  return filteredScheduleData.value.filter(s => {
    const scheduleDate = s.les_date?.includes('-') ? s.les_date : formatScheduleDate(s.les_date);
    return scheduleDate === dateStr && s.les_patient_name === selectedPatient.value;
  });
};

// 시간 차이 계산 (분 단위)
const calculateMinutes = (startTime: string, endTime: string): number => {
  const start = formatTime(startTime).split(':');
  const end = formatTime(endTime).split(':');
  if (start.length < 2 || end.length < 2) return 0;
  
  const startMinutes = parseInt(start[0]) * 60 + parseInt(start[1]);
  const endMinutes = parseInt(end[0]) * 60 + parseInt(end[1]);
  return Math.max(0, endMinutes - startMinutes);
};

// 분을 시간 문자열로 변환
const formatHours = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins > 0) return `${hours}h ${mins}m`;
  return `${hours}h`;
};

// 요양보호사(선택한 사원) 월간 통계
const getCaregiverMonthlyStats = computed(() => {
  const uniqueDays = new Set(filteredScheduleData.value.map(s => s.les_date));
  const uniquePatientsSet = new Set(filteredScheduleData.value.map(s => s.les_patient_name));
  
  let totalMinutes = 0;
  filteredScheduleData.value.forEach(s => {
    totalMinutes += calculateMinutes(s.les_start_time, s.les_end_time);
  });
  
  return {
    totalDays: uniqueDays.size,
    totalHours: formatHours(totalMinutes),
    patientCount: uniquePatientsSet.size
  };
});

// 수급자 월간 통계
const getPatientMonthlyStats = computed(() => {
  if (!selectedPatient.value) return { totalDays: 0, totalHours: '0h', visitCount: 0 };
  
  const patientSchedules = filteredScheduleData.value.filter(s => s.les_patient_name === selectedPatient.value);
  const uniqueDays = new Set(patientSchedules.map(s => s.les_date));
  
  let totalMinutes = 0;
  patientSchedules.forEach(s => {
    totalMinutes += calculateMinutes(s.les_start_time, s.les_end_time);
  });
  
  return {
    totalDays: uniqueDays.size,
    totalHours: formatHours(totalMinutes),
    visitCount: patientSchedules.length
  };
});

// 선택된 수급자의 수가총액
const getPatientTotalSuga = computed(() => {
  if (!selectedPatient.value) return 0;
  
  const patientSchedules = filteredScheduleData.value.filter(s => s.les_patient_name === selectedPatient.value);
  return patientSchedules.reduce((sum, s) => sum + (s.les_suga_money || 0), 0);
});

// 선택된 수급자의 가족요양 정보
const getPatientFamilyInfo = computed(() => {
  if (!selectedPatient.value) return { isFamily: false, who: '' };
  
  const patientSchedule = filteredScheduleData.value.find(s => s.les_patient_name === selectedPatient.value);
  if (patientSchedule && patientSchedule.les_familly_rel === 'Y') {
    return { isFamily: true, who: patientSchedule.les_familly_who || '' };
  }
  return { isFamily: false, who: '' };
});

// 금액 포맷
const formatMoney = (amount: number): string => {
  return amount.toLocaleString('ko-KR');
};

// 담당 수급자 목록 (횟수, 시간 포함)
const getCaregiverPatients = computed(() => {
  const ptData: Record<string, { count: number; minutes: number }> = {};
  
  filteredScheduleData.value.forEach(s => {
    if (!ptData[s.les_patient_name]) {
      ptData[s.les_patient_name] = { count: 0, minutes: 0 };
    }
    ptData[s.les_patient_name].count++;
    ptData[s.les_patient_name].minutes += calculateMinutes(s.les_start_time, s.les_end_time);
  });
  
  return Object.entries(ptData)
    .map(([name, data]) => ({ 
      name, 
      count: data.count,
      hours: formatHours(data.minutes)
    }))
    .sort((a, b) => b.count - a.count);
});

// 월 변경
const changeMonth = (delta: number) => {
  let newMonth = selectedMonth.value + delta;
  let newYear = selectedYear.value;
  
  if (newMonth > 12) {
    newMonth = 1;
    newYear++;
  } else if (newMonth < 1) {
    newMonth = 12;
    newYear--;
  }
  
  selectedMonth.value = newMonth;
  selectedYear.value = newYear;
  fetchScheduleData();
};

// 일정 데이터 조회
const fetchScheduleData = async () => {
  const emp = currentEmployee.value;
  if (!user.value?.m_sisul_code || !emp?.emp_name) {
    return;
  }
  
  try {
    isLoading.value = true;
    
    const url = `/api/files/data/${selectedYear.value}/${selectedMonth.value}/${encodeURIComponent('일정계획')}/${user.value.m_sisul_code}`;
    
    // 특정 요양보호사의 일정만 조회 (이름 + 생년월일로 정확히 매칭)
    const response = await axios.get(url, { 
      params: { 
        caregiverName: emp.emp_name,
        caregiverBirth: emp.emp_birth
      },
      withCredentials: true 
    });
    
    
    if (response.data.success && response.data.data?.rows) {
      scheduleData.value = response.data.data.rows;
      
      // 첫 번째 수급자 자동 선택
      if (uniquePatients.value.length > 0 && !uniquePatients.value.includes(selectedPatient.value)) {
        selectedPatient.value = uniquePatients.value[0];
      }
    } else {
      scheduleData.value = [];
    }
  } catch (error) {
    console.error('일정 데이터 조회 오류:', error);
    scheduleData.value = [];
  } finally {
    isLoading.value = false;
  }
};

// 일정표 PDF 출력
const printSchedulePdf = async () => {
  const emp = currentEmployee.value;
  if (!user.value?.m_sisul_code || !emp?.emp_code) {
    alert('직원 정보가 없습니다.');
    return;
  }
  
  try {
    isPdfLoading.value = true;
    
    const yearMonth = `${selectedYear.value}${String(selectedMonth.value).padStart(2, '0')}`;
    
    const response = await axios.get(
      `/api/beneficiary/caregiver-schedule-pdf/${user.value.m_sisul_code}/${emp.emp_code}/${yearMonth}`,
      { responseType: 'blob' }
    );
    
    // PDF 다운로드
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `일정표_${emp.emp_name}_${yearMonth}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('일정표 PDF 출력 오류:', error);
    alert('일정표 PDF 출력에 실패했습니다.');
  } finally {
    isPdfLoading.value = false;
  }
};

// 전체 요양보호사 일정표 PDF 일괄 출력
const printAllCaregiversPdf = async () => {
  if (!user.value?.m_sisul_code) {
    alert('시설 정보가 없습니다.');
    return;
  }
  
  const caregiverCount = employeeList.value.length;
  if (caregiverCount === 0) {
    alert('출력할 요양보호사가 없습니다.');
    return;
  }
  
  // 확인 메시지
  if (!confirm(`${selectedYear.value}년 ${selectedMonth.value}월 전체 요양보호사(${caregiverCount}명)의 일정표를 PDF로 출력하시겠습니까?\n\n데이터 양에 따라 시간이 소요될 수 있습니다.`)) {
    return;
  }
  
  try {
    isBatchPdfLoading.value = true;
    
    const yearMonth = `${selectedYear.value}${String(selectedMonth.value).padStart(2, '0')}`;
    
    const response = await axios.get(
      `/api/beneficiary/all-caregivers-schedule-pdf/${user.value.m_sisul_code}/${yearMonth}`,
      { responseType: 'blob' }
    );
    
    // PDF 다운로드
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `전체요양보호사_일정표_${yearMonth}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('전체 요양보호사 일정표 PDF 출력 오류:', error);
    alert('전체 요양보호사 일정표 PDF 출력에 실패했습니다.');
  } finally {
    isBatchPdfLoading.value = false;
  }
};

// 닫기
const close = () => {
  dialogVisible.value = false;
};

// 다이얼로그 열릴 때 데이터 조회
watch(dialogVisible, async (visible) => {
  if (visible) {
    // 현재 연월로 초기화
    selectedYear.value = today.getFullYear();
    selectedMonth.value = today.getMonth() + 1;
    selectedPatient.value = '';
    activeView.value = 'caregiver'; // 기본은 해당 사원의 근무일정
    
    // 현재 직원 인덱스 초기화 (props.employee가 목록에 있으면 해당 인덱스로)
    if (props.employees && props.employees.length > 0 && props.employee) {
      const idx = props.employees.findIndex(e => 
        e.emp_code === props.employee.emp_code || 
        (e.emp_name === props.employee.emp_name && e.emp_birth === props.employee.emp_birth)
      );
      currentEmployeeIndex.value = idx >= 0 ? idx : 0;
    } else {
      currentEmployeeIndex.value = 0;
    }
    
    // props.employee가 업데이트될 때까지 대기
    await nextTick();
    
    if (currentEmployee.value?.emp_name) {
      await fetchScheduleData();
    }
  }
});

// 연도 또는 월이 변경될 때 데이터 재조회
watch([selectedYear, selectedMonth], async ([newYear, newMonth], [oldYear, oldMonth]) => {
  // 모달이 열려있고 값이 실제로 변경된 경우에만 조회
  if (dialogVisible.value && (newYear !== oldYear || newMonth !== oldMonth)) {
    await fetchScheduleData();
  }
});

// 수급자 목록 변경 시 선택 값 업데이트
watch(uniquePatients, (patients) => {
  if (patients.length > 0 && !patients.includes(selectedPatient.value)) {
    selectedPatient.value = patients[0];
  }
});
</script>

<style scoped lang="scss">
@import '@/assets/styles/scheduleCalendarModal.scss';
</style>
